<template>
  <v-app-bar
    :clipped-left="$vuetify.breakpoint.lgAndUp"
    app
    color="primary darken-3"
    dark
    class="d-print-none"
    style="z-index: 999"
  >
    <v-progress-linear
      :active="isAppLoading"
      :indeterminate="true"
      absolute
      :top="true"
      color="accent darken-1"
    ></v-progress-linear>

    <v-toolbar-title style="width: 300px" class="ml-0 pl-4">
      <v-app-bar-nav-icon @click.prevent.stop="toggleDrawer" />
      <!-- <span class="hidden-sm-and-down">{{$vuetify.lang.t('$vuetify.base.companyName')}}</span> -->
      <span class="hidden-sm-and-down">{{ companyName }}</span>
    </v-toolbar-title>

    <v-spacer />
    <v-btn
      class="d-none"
      data-cy="securemenu"
      color="primary"
      dark
      v-if="currentUser != null"
      >{{ currentUser.firstName }}</v-btn
    >

    <v-btn @click="hideDrawer" dark icon :to="{ name: 'inventorySearch' }"
      ><v-icon>mdi-folder-search-outline</v-icon></v-btn
    >
    <!-- Live scanning: reads frame after frame with continuous autofocus.
         A single still photo could not reliably read the labels in use. -->
    <qr-scanner :manualProcess="true" @captured="processScannedQr" />

    <!-- Scanning from the top bar now resolves the sticker to its lot and shows
         the same inventory detail the entry camera surfaces, instead of routing
         to a raw "Stored Data" dump. -->
    <!-- Only when a scanned code genuinely matches more than one lot: an LME
         code in stock (43 of 5,795) or a document holding several inventories
         (208 of 14,750). Picking one automatically could open the wrong pile. -->
    <v-dialog v-model="showScanChoice" max-width="460">
      <v-card>
        <v-card-title class="text-subtitle-1">
          {{ $vuetify.lang.t('$vuetify.worksheet.chooseScannedLot') }}
        </v-card-title>
        <v-list dense>
          <v-list-item
            v-for="(m, i) in scanMatches"
            :key="i"
            @click="chooseScanMatch(m)"
          >
            <v-list-item-content>
              <v-list-item-title>{{ m.unitDetail ? m.unitDetail.code : m.inventory.documentNumber }}</v-list-item-title>
              <v-list-item-subtitle>
                #{{ m.inventory.id }} ·
                <template v-if="m.unitDetail">
                  {{ m.inventory.documentNumber }}
                </template>
                <!-- A document tie is two inventories under ONE number, so the
                     number itself tells them apart no better than the id. -->
                <template v-else>
                  {{ m.inventory.productName }} ·
                  {{ m.inventory.quantityAvailable | formatNumber }}
                </template>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card>
    </v-dialog>

    <v-bottom-sheet v-model="showScannedInventory" scrollable>
      <v-card>
        <v-toolbar flat dense>
          <v-toolbar-title class="text-subtitle-1">
            {{ $vuetify.lang.t('$vuetify.worksheet.scannedLot') }}
          </v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="showScannedInventory = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-0">
          <!-- The sticker identifies ONE bundle. Showing only the parent lot
               (53,502 kg across 52 bundles) buries the thing that was scanned,
               so name the bundle and its weight first. -->
          <div v-if="scannedUnit" class="px-4 pt-3 pb-1 text-center">
            <div class="text-caption grey--text text-uppercase">
              {{ $vuetify.lang.t('$vuetify.worksheet.scannedUnit') }}
            </div>
            <div class="text-h6">{{ scannedUnit.code }}</div>
            <div class="text-subtitle-1">
              {{ scannedUnit.amountPerUnit | formatNumber }}
              {{ scannedUnit.unitMeasure === 'WEIGHT_KG' ? 'Kg' : scannedUnit.unitMeasure }}
            </div>
            <v-divider class="mt-3" />
          </div>
          <inventory-details
            :key="scannedInventory ? scannedInventory.id : 0"
            v-model="scannedInventory"
          />
        </v-card-text>
      </v-card>
    </v-bottom-sheet>

    <v-divider class="mx-4" inset vertical></v-divider>
    <v-menu>
      <template v-slot:activator="{ on, attrs }">
        <v-btn class="ml-3" v-bind="attrs" v-on="on" icon>
          <v-chip color="primary">{{ language }}</v-chip>
        </v-btn>
      </template>
      <v-list>
        <v-list-item
          v-for="item in languages"
          :key="item.text"
          @click.stop="changeLanguage(item.value)"
        >
          <v-list-item-title>{{ item.text }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <v-btn
      class="ml-3"
      data-cy="login"
      to="/login"
      v-if="!isLoggedIn"
      icon
      title="Login"
    >
      <v-icon>mdi-login</v-icon>
    </v-btn>
    <v-btn class="ml-3" v-if="isLoggedIn" @click="logout" icon title="Logout">
      <v-icon>mdi-logout</v-icon>
    </v-btn>
  </v-app-bar>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { findInventory } from "@/services/processWorksheet.js";
import { parseLmeQrValue, findAvailableLotsByCode } from "@/utils/lmeLotQr.js";
import { parseQrPayload } from "@/utils/worksheetLabelQr.js";
import { findInventoriesByDocumentNumber } from "@/utils/inventoryDocumentQr.js";
import env from "@/env";
const QrScanner = () => import("@/components/common/QrScanner.vue");
const InventoryDetails = () =>
  import("@/components/inventory/InventoryDetails.vue");

export default {
  data: () => ({
    drawer: null,
    companyName: env.VUE_APP_TITLE,
    scannedInventory: null,
    showScannedInventory: false,
    scannedUnit: null,
    scanMatches: [],
    showScanChoice: false,
  }),
  components: {
    QrScanner,
    InventoryDetails,
  },
  computed: {
    ...mapGetters("navigation", ["isAppLoading"]),
    ...mapGetters("auth", ["currentUser", "isLoggedIn", "isAdministrator"]),
    languages() {
      return [
        {
          text: "ภาษาไทย",
          value: "th",
        },
        {
          text: "English",
          value: "en",
        },
        {
          text: "简体中文",
          value: "zhHans",
        },
      ];
    },
    language() {
      let lang = localStorage.getItem("locale");
      if (lang === "th") {
        return this.$vuetify.lang.t("$vuetify.language.th");
      } else if (lang === "en") {
        return this.$vuetify.lang.t("$vuetify.language.en");
      } else if (lang === "zhHans") {
        return this.$vuetify.lang.t("$vuetify.language.zhHans");
      } else {
        //default to english
        return this.$vuetify.lang.t("$vuetify.language.en");
      }
    },
  },
  methods: {
    ...mapActions("auth", ["processLogout", "setMyCenters"]),
    ...mapActions("navigation", ["toggleDrawer", "hideDrawer", "setCenter"]),
    ...mapActions("messages", ["addErrorMessage"]),
    async resolveLmeSticker(lme) {
      try {
        const matches = await findAvailableLotsByCode(lme);
        if (matches.length === 1) {
          this.scannedInventory = matches[0].inventory;
          this.scannedUnit = matches[0].unitDetail;
          this.showScannedInventory = true;
          return;
        }
        if (matches.length > 1) {
          // Rare (43 of 5,795 in-stock codes). Never guess — let them pick.
          this.scanMatches = matches;
          this.showScanChoice = true;
          return;
        }
        this.addErrorMessage(
          this.$vuetify.lang.t(
            "$vuetify.worksheet.error.lotNotInStock",
            lme.lotCode
          )
        );
      } catch (error) {
        this.addErrorMessage(
          this.$vuetify.lang.t("$vuetify.worksheet.error.noInventoryInfo")
        );
      }
    },
    // A receiving label identifies the whole delivery by its document number
    // rather than one bundle, because one receiving worksheet is one inventory.
    // Same shape as the LME sticker above: open the single match, ask on a tie,
    // say so when there is nothing.
    async resolveScannedDocument(documentNumber) {
      try {
        const matches = await findInventoriesByDocumentNumber({
          documentNumber,
        });
        if (matches.length === 1) {
          this.scannedInventory = matches[0].inventory;
          this.scannedUnit = null;
          this.showScannedInventory = true;
          return;
        }
        if (matches.length > 1) {
          this.scanMatches = matches;
          this.showScanChoice = true;
          return;
        }
        this.addErrorMessage(
          this.$vuetify.lang.t(
            "$vuetify.worksheet.error.noInventoryForDocument",
            documentNumber
          )
        );
      } catch (error) {
        this.addErrorMessage(
          this.$vuetify.lang.t("$vuetify.worksheet.error.noInventoryInfo")
        );
      }
    },
    chooseScanMatch(match) {
      this.showScanChoice = false;
      this.scannedInventory = match.inventory;
      this.scannedUnit = match.unitDetail;
      this.showScannedInventory = true;
    },
    isHttpUrl(value) {
      try {
        const parsed = new URL(value);
        return parsed.protocol === "http:" || parsed.protocol === "https:";
      } catch (error) {
        return false;
      }
    },
    // Mirrors WorksheetEntry.processScannedInventory: resolve the sticker to
    // its lot and show the inventory. Previously this scanner handed off to
    // QrDispatcher -> QrData, which dead-ended on a raw JSON "Stored Data"
    // page whenever anything was unexpected, and never showed lot details.
    async processScannedQr(qrCodeMessage) {
      // A URL payload is a dynamic QR code (uuid-based, possibly access-code
      // protected). Taking manual control of the scan result stopped those
      // routing themselves, so hand them back to the dispatcher that resolves
      // them — otherwise they'd be rejected as unrecognised.
      if (typeof qrCodeMessage == "string" && this.isHttpUrl(qrCodeMessage)) {
        this.$router.push({
          name: "QrDispatcher",
          params: { url: qrCodeMessage },
        });
        return;
      }

      // Our own sticker first. The compact payload is bar-separated and a
      // document number may contain hyphens, which the LME parser would read
      // as a lot index.
      const payload = parseQrPayload(qrCodeMessage);

      // LME stickers stay as plain `BRAND|LOT-CODE` because outside scanners
      // read that. Convert it back to our own record instead of rejecting it.
      if (!payload) {
        const lme = parseLmeQrValue(qrCodeMessage);
        if (lme) {
          await this.resolveLmeSticker(lme);
          return;
        }
        this.addErrorMessage(
          this.$vuetify.lang.t("$vuetify.worksheet.error.unrecognizableQRCode")
        );
        return;
      }

      // A receiving label carries only the document number: the entry lookup
      // below has no entry to resolve, and asking it anyway reported "No
      // inventory information available" for a code that scans perfectly well.
      if (payload.businessType == "Inventory") {
        await this.resolveScannedDocument(payload.documentNumber);
        return;
      }

      const worksheetId = payload.worksheetId;
      const worksheetEntryId = payload.entryId;

      try {
        const resp = await findInventory(worksheetId, worksheetEntryId);
        if (resp && resp.data) {
          this.scannedInventory = resp.data;
          // find-inventory resolves the exact bundle from the sticker
          this.scannedUnit = resp.data.selectedUnitDetail || null;
          this.showScannedInventory = true;
          return;
        }
      } catch (error) {
        // fall through to the worksheet view below
      }

      // No inventory yet — an Input worksheet, or one not processed to USED.
      // The entry detail page still has something useful to show, so go there
      // rather than reporting a dead end.
      if (worksheetId) {
        this.$router.push({
          name: "WorksheetScan",
          params: { id: worksheetId, entryId: worksheetEntryId },
        });
      } else {
        this.addErrorMessage(
          this.$vuetify.lang.t("$vuetify.worksheet.error.noInventoryInfo")
        );
      }
    },
    logout() {
      this.$axios.post(env.VUE_APP_AUTHHOST + "/signout").then(() => {
        this.processLogout();
        delete this.$axios.defaults.headers.common["Authorization"];
        this.$router.push({ name: "Login" });
      });
    },
    changeLanguage(language) {
      this.language = language;
      localStorage.setItem("locale", language);
      window.location.reload();
    },
  },
};
</script>

<style>
</style>